import { useCallback } from 'react';
import { useFormikContext } from 'formik';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';
import { confirmAsync } from '@components/Modals/ConfirmPopup';
import { saveBeforeEditLocationMessage } from '@forms/types';

interface EditLocationOptions {
	/** When set, saving the location also updates the legs on this job that use it */
	updateLegsForJobId?: number;
}

/*
 * Hook used for opening Consignee Maintenance for a location from within a Formik form.
 * A location can only be edited from a clean form, so when the form has unsaved changes the user is prompted to save first. Consignee
 * Maintenance only opens once the save succeeds. If the user declines or the save fails, the edit is abandoned and any errors display
 * on the form as normal. The form's onSubmit must resolve to true on a successful save for the edit to continue.
 */
export default function useEditLocation({ updateLegsForJobId }: EditLocationOptions = {}) {
	const appDispatch = useAppDispatch();
	const { dirty, submitForm } = useFormikContext();
	/** Formik types submitForm as void, but it resolves with whatever the form's onSubmit returns. */
	const submitFormAsync = submitForm as () => Promise<boolean | undefined>;

	return useCallback(
		async (locationId: string) => {
			if (dirty) {
				const shouldSave = await confirmAsync(saveBeforeEditLocationMessage);
				if (!shouldSave) {
					return;
				}

				const isSaved = await submitFormAsync();
				if (!isSaved) {
					return;
				}
			}

			appDispatch(
				queueModal({
					Component: 'consigneeModal',
					Props: { id: locationId, updateLegsForJobId }
				})
			);
		},
		[dirty, submitFormAsync, appDispatch, updateLegsForJobId]
	);
}
